import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  Row,
  Col,
  Form,
  Input,
  Tabs as Tab,
  Select,
  Button,
  DatePicker,
  Switch,
} from 'antd'
import { Formik } from 'formik'
import { SearchOutlined, SaveOutlined } from '@ant-design/icons'

import Tabs from '../../Tabs'

import { selectAllRetrievers, getRetrievers } from '../../../../state/modules/retrievers'
import useDispatchHttp from '../../../../hooks/dispatchHttpHandler'
import { DATE_FORMAT } from '../../../../constants'
import {
  sortOptions,
  mainRetrieverPastColumns,
  mainRetrieverColumns,
} from '../../../../data'
import Table from '../../Tables/SearchTable'

const TABS = {
  PAST: 'past',
  ALL: 'all',
}

const SetRetrieverForm = ({
  initialValues,
  onSubmit,
  entityId,
  onClose,
  isUserActionAllowed,
  isAutoDetection,
}) => {
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
  })
  const [searchParams, setSearchParams] = useState({
    facility_profile: initialValues.id,
    ordering: 'last_case_date',
  })

  const [pastTabOrdering, setPastTabOrdering] = useState('last_case_date')

  const [isAutoDetectionEnabled, setIsAutoDetectionEnabled] = useState(isAutoDetection)
  const [selectedRowKeys, setSelectedRowKeys] = useState([initialValues.main_retriever])
  const [preparedRetrieversList, setPreparedRetrieversList] = useState([])
  const [activeTab, setActiveTab] = useState(TABS.PAST)
  const [columns, setColumns] = useState(mainRetrieverPastColumns)
  const dispatchHttp = useDispatchHttp()
  const retrieversList = useSelector(selectAllRetrievers)
  const { count } = useSelector(state => state.retrievers)
  const loadingStatus = useSelector(state => state.retrievers.status)

  const handleLoadDataWrapper = useCallback(
    payload => {
      const finalPayload = { ...payload }

      if (activeTab === TABS.PAST && !finalPayload.ordering) {
        finalPayload.ordering = pastTabOrdering
      }

      return getRetrievers(finalPayload)
    },
    [activeTab, pastTabOrdering]
  )

  const handleSearch = useCallback(
    e => {
      const { value } = e.target
      const newPagination = { ...pagination, page: 1 }

      setPagination(newPagination)
      setSearchParams(state => ({ ...state, search: value }))

      const paramsToSend = {
        ...searchParams,
        search: value,
        ...newPagination,
      }

      if (activeTab === TABS.PAST) {
        paramsToSend.ordering = pastTabOrdering
      }
      dispatchHttp(getRetrievers(paramsToSend))
    },
    [dispatchHttp, searchParams, pagination, activeTab, pastTabOrdering]
  )

  const handleSelect = useCallback(
    value => {
      setPastTabOrdering(value)
      setSearchParams(state => ({ ...state, ordering: value }))

      const params = { ...searchParams, ordering: value }
      dispatchHttp(getRetrievers(params))
    },
    [dispatchHttp, searchParams]
  )

  const handleKeyPress = e => {
    if ((e.charCode || e.keyCode) === 13) {
      e.preventDefault()
    }
  }

  const handleChangeTab = tab => {
    const newPagination = {
      page: 1,
      pageSize: 10,
    }
    setPagination(newPagination)

    let params
    if (tab === TABS.PAST) {
      params = {
        facility_profile: initialValues.id,
        ordering: pastTabOrdering,
      }
    } else {
      params = {
        facility_profile: null,
        date_range: null,
        ordering: null,
      }
    }

    setSearchParams(state => ({ ...state, ...params }))
    dispatchHttp(getRetrievers({ ...searchParams, ...params, ...newPagination }))
    setActiveTab(tab)
  }

  const handleSelectRange = (dates, datesString) => {
    const date_range = datesString.filter(date => !!date).toString()

    setSearchParams(state => ({ ...state, date_range }))

    const params = { ...searchParams, date_range, ordering: pastTabOrdering }
    dispatchHttp(getRetrievers(params))
  }

  useEffect(() => {
    dispatchHttp(
      getRetrievers({
        facility_profile: initialValues.id,
        ordering: 'last_case_date',
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatchHttp, initialValues])

  useEffect(() => {
    const currentSelectedId = selectedRowKeys[0] || initialValues.main_retriever

    const data = retrieversList.map(item => ({
      ...item,
      isMainRetriever: item.id === currentSelectedId,
    }))
    setPreparedRetrieversList(data)
  }, [retrieversList, initialValues, selectedRowKeys])

  useEffect(() => {
    if (activeTab === TABS.PAST) {
      setColumns(mainRetrieverPastColumns)
    } else {
      setColumns(mainRetrieverColumns)
    }
  }, [activeTab])

  const list = (
    <Table
      columns={columns}
      totalData={preparedRetrieversList}
      onLoadData={handleLoadDataWrapper}
      formData={searchParams}
      loadingStatus={loadingStatus}
      rowKey="id"
      customRowSelection={{
        key: 'isMainRetriever',
        disabled:
          !isUserActionAllowed('update_retrievers_facilityprofile') ||
          !isAutoDetectionEnabled,
        onSelect: newSelectedRowKeys => {
          setSelectedRowKeys([newSelectedRowKeys])
        },
      }}
      totalCount={count}
      onPageChange={setPagination}
      pageSize={pagination.pageSize}
      currentPage={pagination.page}
    />
  )

  return (
    <Formik
      initialValues={{ main_retriever: initialValues.main_retriever }}
      onSubmit={async (_, { setSubmitting }) => {
        try {
          await onSubmit(
            entityId,
            { main_retriever: selectedRowKeys && selectedRowKeys[0] },
            isAutoDetectionEnabled
          )
          onClose()
        } catch (err) {
          setSubmitting(false)
        }
      }}
    >
      {({ values, handleSubmit, isSubmitting }) => (
        <Form onFinish={handleSubmit} initialValues={values} onKeyDown={handleKeyPress}>
          <Form.Item>
            <Input
              prefix={<Button type="link" size="small" icon={<SearchOutlined />} />}
              placeholder="Name, Contname, CMS ID"
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Tabs
            defaultActiveKey="past"
            fullWidth
            theme="dark"
            tabBarExtraContent={
              <Form layout="inline" className="sort-form" component="div">
                <Row gutter={32}>
                  {isUserActionAllowed('update_main_retriever_auto_detection') && (
                    <Col>
                      <Form.Item
                        label="Auto Detection"
                        className="sort-form__select-field"
                      >
                        <Switch
                          defaultChecked={isAutoDetectionEnabled}
                          checked={isAutoDetectionEnabled}
                          name="auto_detection"
                          key="switch"
                          onChange={checked => setIsAutoDetectionEnabled(checked)}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {activeTab === TABS.PAST && (
                    <>
                      <Col>
                        <DatePicker.RangePicker
                          format={DATE_FORMAT.year_month_day}
                          onChange={handleSelectRange}
                        />
                      </Col>
                      <Col>
                        <Form.Item label="Sort by" className="sort-form__select-field">
                          <Select
                            size="middle"
                            className="sort-form__select"
                            onChange={handleSelect}
                            options={sortOptions}
                            placeholder="Select here"
                            value={pastTabOrdering}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              </Form>
            }
            tabsProps={{
              size: 'small',
              tabBarGutter: 24,
              onChange: handleChangeTab,
            }}
          >
            <Tab.TabPane tab="Past Retrievers" key={TABS.PAST}>
              {list}
            </Tab.TabPane>
            <Tab.TabPane tab="All" key={TABS.ALL}>
              {list}
            </Tab.TabPane>
          </Tabs>
          <Row justify="space-between">
            <Col>
              <Button onClick={onClose}>Close window</Button>
            </Col>
            <Col>
              {(isUserActionAllowed('update_retrievers_facilityprofile') ||
                isUserActionAllowed('update_main_retriever_auto_detection')) && (
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Save Changes
                </Button>
              )}
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  )
}

export default SetRetrieverForm
